/**
 * @module app.cm
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { apiCall } from 'app/shared/lib/essette-api';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { Toaster } from 'app/shared/lib/toaster';
import { deepClone } from 'app/shared/lib/utilities';
import { getReference } from 'app/shared/models/essette-types';
import { validate } from 'app/shared/elements/ess-rule';
import { CaseService } from 'app/cm/services/case-service';
import { HouseholdCaseAssignmentHistoryGrid } from 'app/cm/components/household/household-case-assignment-history-grid';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { IUserSearchFilters, UserService } from 'app/core/services/user-service';
import * as Core from 'app/core/models/essette-api';
import * as CM from 'app/cm/models/essette-api';

@autoinject()
export class EditHouseholdCaseAssignment extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull({ changeHandler: 'reload' })
    protected assignedTo: Core.User | null = null;

    protected editedAssignedTo: Core.User | null = null;

    @bindable.objectNull({ defaultBindingMode: bindingMode.twoWay })
    protected householdCase: CM.HouseholdCase | null = null;

    protected users: ReadonlyArray<Core.User> = [];

    @bindable.boolean
    protected readonly: boolean = true;

    protected validate = validate;


    ////****************** Element References ***************************************////
    protected $assignmentHistory!: HouseholdCaseAssignmentHistoryGrid;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: CaseService,
        private readonly userService: UserService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.reload();
    }


    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        await this.loadHouseholdCaseOwners();
    }


    ////****************** Functions ************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public reset(): void {
        this.editedAssignedTo = null;
    }

    public async submit(): Promise<boolean> {
        return await this.reassignHouseholdCase();
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadHouseholdCaseOwners(): Promise<void> {
        try {
            this.state.beginLoad();

            const options: Partial<IQueryLoadOptions> = {
                all: true
            };

            const searchFilters: IUserSearchFilters = {
                notInternal: true,
                hasNoExternalSystem: true
            }

            const response = await this.userService.loadUsers(options, [], searchFilters);
            this.users = response.results;
            if(this.householdCase?.assignedTo?.id) {
                this.users = this.users.filter(user => user.id !== this.householdCase?.assignedTo?.id);
            }
        }
        catch(error) {
            this.logger.error('Error Loading Users', error);
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async reassignHouseholdCase(): Promise<boolean> {
        if(!this.editedAssignedTo || !this.householdCase) return false;

        const saveHouseholdCase: CM.HouseholdCase = {
            id: this.householdCase.id,
            assignedTo: getReference(this.editedAssignedTo)
        }

        let isSuccessful: boolean = false;
        try {
            this.state.beginSave();
            const response = await this.service.assignHouseholdCase(saveHouseholdCase);
            isSuccessful = response.isSuccessful;
            if(isSuccessful) {
                this.householdCase.assignedTo = deepClone(this.editedAssignedTo);
                this.editedAssignedTo = null;
                this.$assignmentHistory.reload();
                this.loadHouseholdCaseOwners();
            }
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.assigned('cm:householdCase.$', isSuccessful);
            this.state.endSave();
            return isSuccessful;
        }
    }
}
